import { useState, useCallback } from 'react';
import { router, Link, usePage } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';

/**
 * Shared page props from Inertia.
 */
interface SharedPageProps {
    unansweredQuestionsCount?: number;
    [key: string]: unknown;
}

/**
 * Newspaper article data from the backend.
 */
interface NewspaperArticle {
    id: number;
    issue_number: number;
    title: string;
    slug: string;
    byline: string;
    excerpt: string;
    cover_image_url: string | null;
    is_published: boolean;
    published_at: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

/**
 * Pagination link from Laravel.
 */
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

/**
 * Paginated articles response.
 */
interface PaginatedArticles {
    data: NewspaperArticle[];
    links: PaginationLink[];
    last_page: number;
}

/**
 * Props for the Newspaper Index page.
 */
interface Props {
    articles: PaginatedArticles;
    filters: {
        search: string | null;
        published: string | null;
    };
}

/**
 * GM Newspaper Index page - displays all newspaper articles with management actions.
 */
export default function Index({ articles, filters }: Props) {
    const [search, setSearch] = useState(filters.search || '');
    const [publishedFilter, setPublishedFilter] = useState(filters.published || '');

    // Get unanswered questions count from shared page props
    const { unansweredQuestionsCount = 0 } = usePage<SharedPageProps>().props;

    // Handle search/filter submission
    const handleFilter = useCallback(() => {
        router.get('/gm/newspaper', {
            search: search || undefined,
            published: publishedFilter || undefined,
        }, { preserveState: true });
    }, [search, publishedFilter]);

    // Clear all filters
    const handleClearFilters = () => {
        setSearch('');
        setPublishedFilter('');
        router.get('/gm/newspaper', {}, { preserveState: true });
    };

    // Handle delete
    const handleDelete = (article: NewspaperArticle) => {
        if (confirm(`Are you sure you want to delete "${article.title}"?`)) {
            router.delete(`/gm/newspaper/${article.id}`, { preserveScroll: true });
        }
    };

    // Handle toggle publish
    const handleTogglePublish = (article: NewspaperArticle) => {
        router.patch(`/gm/newspaper/${article.id}/toggle-publish`, {}, { preserveScroll: true });
    };

    return (
        <GmLayout title="The Feywild Times" activePath="/gm/newspaper">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">📰 The Feywild Times</h1>
                    <div className="flex gap-2">
                        <Link
                            href="/gm/newspaper/comics"
                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Features
                        </Link>
                        <Link
                            href="/gm/newspaper/questions"
                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors inline-flex items-center gap-2"
                        >
                            Questions
                            {unansweredQuestionsCount > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-bold text-white bg-amber-500 rounded-full">
                                    {unansweredQuestionsCount > 99 ? '99+' : unansweredQuestionsCount}
                                </span>
                            )}
                        </Link>
                        <Link
                            href="/gm/newspaper/create"
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                        >
                            + New Article
                        </Link>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[200px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                                placeholder="Title, content, or byline"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>

                        {/* Published Filter */}
                        <div className="w-40">
                            <label className="block text-sm text-gray-400 mb-1">Status</label>
                            <select
                                value={publishedFilter}
                                onChange={(e) => setPublishedFilter(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All</option>
                                <option value="true">Published</option>
                                <option value="false">Draft</option>
                            </select>
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex gap-2">
                            <button
                                onClick={handleFilter}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {/* Articles Table */}
                {articles.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        No articles found. Create your first article to start publishing The Feywild Times.
                    </div>
                ) : (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Issue</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Title</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Byline</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {articles.data.map((article) => (
                                        <ArticleRow
                                            key={article.id}
                                            article={article}
                                            onDelete={handleDelete}
                                            onTogglePublish={handleTogglePublish}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {articles.last_page > 1 && (
                    <Pagination links={articles.links} />
                )}
            </div>
        </GmLayout>
    );
}

/**
 * ArticleRow - Displays a single article row with actions.
 */
function ArticleRow({
    article,
    onDelete,
    onTogglePublish,
}: {
    article: NewspaperArticle;
    onDelete: (article: NewspaperArticle) => void;
    onTogglePublish: (article: NewspaperArticle) => void;
}) {
    return (
        <tr>
            {/* Issue Number */}
            <td className="px-4 py-3 text-sm text-gray-400">
                #{article.issue_number}
            </td>

            {/* Title */}
            <td className="px-4 py-3">
                <div>
                    <Link href={`/gm/newspaper/${article.id}/edit`} className="text-white font-medium hover:text-purple-400">
                        {article.title}
                    </Link>
                    <p className="text-xs text-gray-500 truncate max-w-xs">{article.excerpt}</p>
                </div>
            </td>

            {/* Byline */}
            <td className="px-4 py-3 text-sm text-gray-400">
                {article.byline}
            </td>

            {/* Status */}
            <td className="px-4 py-3">
                <span className={`px-2 py-1 text-xs rounded ${article.is_published ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'}`}>
                    {article.is_published ? 'Published' : 'Draft'}
                </span>
            </td>

            {/* Actions */}
            <td className="px-4 py-3">
                <div className="flex gap-2">
                    <Link
                        href={`/gm/newspaper/${article.id}/edit`}
                        className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                    >
                        Edit
                    </Link>
                    <button
                        onClick={() => onTogglePublish(article)}
                        className={`px-3 py-1 text-xs rounded transition-colors ${
                            article.is_published
                                ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                    >
                        {article.is_published ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                        onClick={() => onDelete(article)}
                        className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                    >
                        Delete
                    </button>
                </div>
            </td>
        </tr>
    );
}

/**
 * Pagination - Displays pagination links.
 */
function Pagination({ links }: { links: PaginationLink[] }) {
    return (
        <div className="mt-6 flex justify-center gap-1">
            {links.map((link, index) => (
                <a
                    key={index}
                    href={link.url || '#'}
                    className={`px-3 py-1 rounded text-sm ${
                        link.active
                            ? 'bg-purple-600 text-white'
                            : link.url
                            ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                            : 'bg-slate-800 text-gray-500 cursor-not-allowed'
                    }`}
                    dangerouslySetInnerHTML={{ __html: link.label }}
                />
            ))}
        </div>
    );
}

